/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {ColDef, ColumnState} from 'ag-grid-community';
import _ from 'lodash';
import cloneDeep from 'lodash.clonedeep';
import invert from 'lodash.invert';
import isEmpty from 'lodash.isempty';
import isNull from 'lodash.isnull';
import isUndefined from 'lodash.isundefined';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {translate} from '@ion/core/src/locale/locale';
import ApplicationFeatures from '@ion/core/src/security/ApplicationFeatures';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import SmartSearchPaging from '@ion/core/src/services/SmartSearchPaging';
import {
  AccountLink,
  CRUDPermissions,
  CTCEntity,
  Device,
  Devices,
  EntityStatus,
  GenericObject,
  Link
} from '@ion/core/src/types/entities';
import {DeviceSortTypes} from '@ion/core/src/types/services';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {booleanToString} from '@ion/core/src/utils/BooleanUtils';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {
  addSlashesToQuery,
  getEdgeAppVersion,
  getJpodConfig,
  getJpodFirmware,
  getMultipleSearchKeywords,
  getPendingEdgeHosted,
  getPendingFirmware,
  getPendingJpodConfig,
  getPendingJpodFW,
  getPendingScript,
  getVersionByPlatformId,
  isLMUDevice as isValidLMUDevice,
  isMDTDevice as isValidMDTDevice,
  PLATFORM_IDS
} from '@ion/core/src/utils/DeviceUtils';
import {numberSortComparator} from '@ion/core/src/utils/SortUtils';
import ChargeLevel from '@ion/dls/src/components/chargelevel/ChargeLevel';
import {LinkCell} from '@ion/dls/src/components/grid/LinkCell';
import {AssetTreeRefresh} from '@ion/dls/src/editors/AssetEditor';
import {checkEOLDevice, getSpreadVal} from '@ion/dls/src/editors/DeviceEditorUtils';
import {getFilterValue} from '@ion/dls/src/editors/grids/DeviceDetailViewGrid';
import {getUniqItems} from '@ion/dls/src/editors/ListEditorUtils';
import {
  defaultNumberFilterParams,
  defaultStatusFilterParams,
  defaultTextFilterParams
} from '@ion/dls/src/grid/ColumnDefinitions';
import {definitionsToState, resolveDefinitions} from '@ion/dls/src/grid/ColumnResolver';
import {ExportClient} from '@ion/dls/src/grid/Exporter';
import {
  BulkHeader, GridOperation, IonGrid, IonGridAPI,
  ViewInheritableGroupProps
} from '@ion/dls/src/grid/IonGrid';
import {
  FilterModel,
  SelectAllCheckbox,
  SortModelType
} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {useColumnChooser} from '@ion/dls/src/grid/useColumnChooser';
import {useColumnState} from '@ion/dls/src/grid/useColumnState';
import {useLoadGridData} from '@ion/dls/src/grid/useLoadGridData';
import {useBulkOperationResults} from '@ion/dls/src/hooks/useBulkOperationResults';
import {useClearFilters} from '@ion/dls/src/hooks/useClearFilters';
import {useCRUDOperationResults} from '@ion/dls/src/hooks/useCRUDOperationResults';
import {useGridDataUpdate} from '@ion/dls/src/hooks/useGridDataUpdate';
import useGlobalProps from '@ion/dls/src/props/GlobalPropsConfig';
import {DLSTheme, GridPageSizeProps, ViewSubGroupsProps} from '@ion/dls/src/types/types';
import {dateTimeFormatter, translateStatus} from '@ion/dls/src/utils/ComponentUtils';
import {
  getStatusFilterParams,
  processExportColumns,
  verifySupplementalCalls
} from '@ion/dls/src/utils/GridUtils';
import {
  createModularSearchFilters,
  getQueryValue,
  hasANDkey,
  hasORkey
} from '@ion/features/src/search/utils';
import useStopPageMonitor from '@ion/telemetry/src/apm/hooks/useStopPageMonitor';
import {useCurrentGlobalGroupId} from '../../hooks/useCurrentGlobalGroupId';
import {useGroupRefresh} from '../../hooks/useGroupRefresh';
import {useAuthenticatedUser} from '../../pages/admin/users/useAuthenticatedUser';
import {exportDevices} from '../../store/actions/exporter';
import {dispatchGridColumnsChange, sortColumns} from '../../store/helpers';
import {gridDefaultPageSize} from '../../utils/helpers';
import {AccountCell} from '../admin/accounts/AccountCell';
import {GridOperations} from '../admin/AdminContainer';
import {assetEditResultSelector} from '../admin/assets/selector';
import {viewAdminAsset} from '../admin/assets/state';
import {MAX_VIEW_DEVICES} from './BulkViewDevicesDialog';
import {getViewDeviceTabs} from './BulkViewDeviceTabs';
import DeviceGroupInheritableLinks from './DeviceGroupInheritableLinks';
import {DeviceGroupLinks} from './DeviceGroupLinks';
import {DeviceRowEditor} from './DeviceRowEditor';
import {DEFAULT_SORT_PARAM} from './Devices';
import {DevicesActionMenu} from './DevicesActionMenu';
import {EolCell} from './EolCell';
import {getICNInfo} from './helpers';
import JobIdLinks from './JobIdLinks';
import {
  createModularSearchQueryString,
  getExcludeDevicesQuery,
  getNewSearchCriteria,
  hasAnySearch,
  hasMaxSearch
} from './ModularSearchUtils';
import PendingEdgeHostedCell from './PendingEdgeHostedCell';
import PendingJobIdLinks from './PendingJobIdLinks';
import PendingJpodConfigLinks from './PendingJpodConfigLinks';
import {
  devicesGridColumnStateSelector,
  devicesModularSearchSelector
} from './selector';
import {
  DEFAULT_SMART_SEARCH_QUERY,
  deleteAdminDevices,
  devicesGridRefreshed,
  editAdminDevices,
  getDevicesBySmartSearch,
  getVin,
  loadDeviceDetail,
  NAME,
  SET_GRID_COLUMNS,
  setDevicesTabs,
  setFilterModel,
  setSelectedDevicesTab,
  showBulkViewDevices,
  viewAdminDevice,
  viewDevice
} from './state';
import {devicesGridRefreshSelector} from './state-selectors';
import {SearchFields} from './types';
import {
  formatEnabledServices,
  getConfigVersion,
  getDeviceIdentifier,
  getFileStatus,
  getFirmwareVersion,
  getMDN,
  getVbusStatus
} from './ViewDeviceEditorUtils';

export interface DevicesGridProps extends
  ViewSubGroupsProps,
  ViewInheritableGroupProps,
  SelectAllCheckbox,
  GridOperation,
  GridOperations,
  GridPageSizeProps,
  BulkHeader,
  AssetTreeRefresh {
  onSelectAccount?: (value: AccountLink) => void;
  getTotalCount?: (totalCount: number) => void;
  isCtcAdmin?: boolean;
  isIonSuite?: boolean;
  includeDefaultDefinitions?: boolean;
  isHeartBeat?: boolean;
  enableAllHyperLinks?: boolean;
  enabledHyperLinks?: string[];
  excludedColumns?: string[];
  filterableColumns?: string[];
  rowMenusEnabled?: boolean;
  omitHeader?: boolean;
  omitHeaderCTAs?: boolean;
  enableSelectionsExport?: boolean;
  exportLimit?: number;
  isMultiEditEnabled?: boolean;
  includeGlobalSearch?: boolean;
  includeSwapDevice?: boolean;
  searchKeyword?: string;
  selectedDevices: Devices;
  setSelectedDevices?: Function;
  filterModel?: FilterModel;
  rowPerPageTextTransform?: any;
  usePreferedPageSize?: boolean;
  globalGroup?: Link;
  onSCIUpdateClick?: Function;
  areTotalRowsSelected?: boolean;
  searchFilters?: any;
  showBulkHeader?: boolean;
  isSortByParams?: boolean;
  accountsPermissions?: CRUDPermissions;
  refreshInitialization?: Function;
  devicesPermissions?: CRUDPermissions;
  hasVehicleProfile?: boolean;
  hasCreateJob?: boolean;
  selectedItemsCount?: number;
  hasExportLimit?: boolean;
  showEOLDevices?: boolean;
  platformResults?: GenericObject[];
  setSortOn?: Function;
  anySearch?: GenericObject;
  sortOn?: string[];
  hasMultiColumnPreferences?: boolean;
  excludedRows?: CTCEntity[];
  setExcludedRows?: (rows) => void;
  enableExclude?: boolean;
  enableDefaultSort?: boolean;
  reloadOnVisible?: string[];
  hasInheritableGroups?: boolean;
  Features?: ApplicationFeatures;
  isVbuFwEnabled?: boolean;
  setIsGridLoaded?: Function;
}

// Default excluded columns.
export const EXCLUDED_COLUMNS_DEFAULT = [];

// Default filterable columns.
export const FILTERABLE_COLUMNS_DEFAULT = [
  'deviceId',
  'deviceName',
  'identifierUI',
  'statusUI',
  'airId',
  'esn',
  'serialNumber'
];

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    batteryCell : {
      marginTop : '5px',
      padding   : '0px !important',
      display   : 'flex'
    }
  };
});

/**
 * Grid to be rendered on the Admin Devices page.
 */
export const DevicesGrid = ({
  onSelectAccount,
  getAreTotalRowsSelected,
  toggleViewSubGroups,
  getTotalCount,
  onBulkActionsClick,
  selectedDevices,
  setSelectedDevices,
  filterModel,
  globalGroup,
  onSCIUpdateClick,
  setGridPageSize,
  accountsPermissions,
  refreshInitialization,
  devicesPermissions,
  hasVehicleProfile,
  platformResults,
  hasCreateJob,
  sortOn,
  setSortOn,
  anySearch,
  excludedRows,
  setExcludedRows,
  setIsGridLoaded,
  searchKeyword = '',
  gridPageSize = gridDefaultPageSize,
  excludedColumns = EXCLUDED_COLUMNS_DEFAULT,
  filterableColumns = FILTERABLE_COLUMNS_DEFAULT,
  showTreeRefresh = false,
  enableExclude = false,
  rowMenusEnabled = true,
  omitHeader = false,
  omitHeaderCTAs = false,
  enableSelectionsExport = false,
  enableAllHyperLinks = false,
  enabledHyperLinks = [],
  isHeartBeat = false,
  isMultiEditEnabled = false,
  isCtcAdmin = false,
  hasInheritableGroups = false,
  isIonSuite = false,
  includeDefaultDefinitions = false,
  hideSubGroups = false,
  includeSwapDevice = false,
  includeGlobalSearch = false,
  enableSelectAllCheckbox = false,
  usePreferedPageSize = false,
  isVbuFwEnabled = false,
  viewSubGroups = false,
  viewInheritableGroup = false,
  isSortByAlphaNumeric = false,
  areTotalRowsSelected = false,
  showBulkHeader = false,
  isSortByParams = false,
  isPersist = false,
  enableDefaultSort = false,
  hasExportLimit = false,
  showEOLDevices = false,
  reloadOnVisible = [],
  hasMultiColumnPreferences = false,
  exportLimit = 0,
  selectedItemsCount = 0,
  rowPerPageTextTransform = 'uppercase',
  searchFilters,
  Features
}: DevicesGridProps, ref) => {
  const pageMonitorComplete                   = useStopPageMonitor();
  const dispatch                              = useDispatch();
  const applications                          = useAuthenticatedUser()?.applications;
  const modularSearchParams                   = useSelector(devicesModularSearchSelector);
  const columnState: ColumnState[]            = useSelector(devicesGridColumnStateSelector);
  const editedAssetResult                     = useSelector(assetEditResultSelector);
  const ionGridRef                            = useRef<IonGridAPI>();
  const [refreshRequired, setRefreshRequired] = useState(false);
  const [gridApi, setGridApi]                 = useState(null);
  const [isColumnChanged, setIsColumnChanged] = useState(false);
  const [totalRowsCount, setTotalRowsCount]   = useState<number>();
  const refreshGrid                           = useGroupRefresh();
  const onClearFilters                        = useClearFilters(ionGridRef);
  const isICNUser                             = getICNInfo(isHeartBeat, applications);
  const classes                               = useStyles();
  const maxLimit                              = SmartSearchPaging.MAX_LIMIT;
  const gridExportCount                       = areTotalRowsSelected ? totalRowsCount
    : selectedItemsCount || totalRowsCount;
  const {editResult}                          = useCRUDOperationResults(NAME);
  const accountID                             = useCurrentGlobalGroupId();

  const {
    hasUpdatePermission,
    hasDeletePermission
  } = devicesPermissions || {};

  const {bulkEditResult}  = useBulkOperationResults(NAME);
  const refreshDeviceGrid = useSelector(devicesGridRefreshSelector);

  const defaultColumnDefinitions = useMemo(() =>
    getColumnDefinitions({
      excludedColumns,
      filterableColumns,
      enableAllHyperLinks,
      includeGlobalSearch,
      enabledHyperLinks,
      isCtcAdmin,
      isIonSuite,
      isHeartBeat,
      hasVehicleProfile,
      isICNUser,
      showEOLDevices,
      isSortByAlphaNumeric,
      isVbuFwEnabled
    }),
  [
    excludedColumns,
    filterableColumns,
    enableAllHyperLinks,
    includeGlobalSearch,
    enabledHyperLinks,
    isCtcAdmin,
    isIonSuite,
    isHeartBeat,
    hasVehicleProfile,
    isICNUser,
    showEOLDevices,
    isSortByAlphaNumeric,
    isVbuFwEnabled
  ]);

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : defaultColumnDefinitions,
    columnState
  });

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser((includeGlobalSearch || includeDefaultDefinitions) ? defaultDefinitions : definitions);

  const usePreferredDateTimeFormater     = useGlobalProps('DevicesGrid.usePreferredDateTimeFormater') as boolean;
  const anySearchForOnlyDisplayedColumns = useGlobalProps('DevicesGrid.anySearchForOnlyDisplayedColumns') as boolean;

  useGridDataUpdate(NAME, ionGridRef.current, devicesGridRefreshed);

  useEffect(() => {
    if (gridApi && editedAssetResult) {
      const {id: assetId, name: assetName} = editedAssetResult;
      gridApi.forEachNode((row: {data: GenericObject}) => {
        const data = row?.data;
        const asset = data?.asset;
        if (asset && entityId(asset.href) === assetId && assetName !== asset.title) {
          ionGridRef?.current?.updateRowNodes?.([{
            ...data,
            asset : {
              ...asset,
              title : assetName
            }
          }]);
        }
      });
    }
  }, [editedAssetResult]);

  const getExportColumnDefinitions = useCallback(() => {
    const ionGridData = ionGridRef?.current?.getExportParams();
    const {columns, sortOn} = ionGridData;
    const customColumnBuilder = {};
    const nameMappingColumns = {
      deviceType          : 'typeName',
      identifierUI        : 'identifier',
      assetName           : 'asset',
      pegBehaviorName     : 'pegBehavior',
      enabledServicesUI   : 'enabledServices',
      createdOnUI         : 'createdOn',
      lastAvlCommUI       : 'lastAvlCommunication.locationTime',
      vbusStatusUI        : 'vbusStatus',
      vinUI               : 'vin',
      pendingScriptUI     : 'newVersion',
      pendingFirmwareUI   : 'fileHistoryPending.newVersion',
      eolUI               : 'eol',
      androidOSVersion    : 'extensions.androidOSVersion',
      kernelVersion       : 'extensions.kernelVersion',
      pendingJpodFWUI     : 'pendingJpodFw',
      pendingJpodConfigUI : 'pendingJpodConfig',
      pendingEdgeHostedUI : 'pendingEdgeApp',
      jpodFirmwareUI      : 'jpodFirmware',
      jpodConfigUI        : 'jpodConfig',
      ble3FirmwareUI      : 'ble3Firmware',
      vbu3FirmwareUI      : 'vbu3Firmware',
      bspModemFirmwareUI  : 'bspModemFirmware',
      cellRadioFirmwareUI : 'cellRadioFirmware',
      edgeAppVersionUI    : 'edgeAppVersion',
      mobileIdUI          : 'mobileId',
      mdtUI               : 'mdt',
      lmuUI               : 'lmu',
      vbusModeUI          : 'extensions.vbusMode',
      batteryPercentage   : 'extensions.batteryPercentageLeft',
      peripherals         : 'features.ATTACHMENTS',
      vehicleProfile      : 'vinInfo',
      configStatusUI      : 'configStatus',
      firmwareStatusUI    : 'firmwareStatus'
    };
    columns.forEach(column => {
      if (column.headerName) {
        const fieldLabel = nameMappingColumns[column.field] || attributeMappings[column.field] || column.field;
        customColumnBuilder[fieldLabel] = column.headerName;
      }
    });
    return {columns: customColumnBuilder, sortOn};
  }, []);

  // Provides reference to the grid's api.
  const onGridReady = useCallback((api) => {
    setGridApi(api);
  }, []);

  /**
   * Returns a reference to the grids public api ..
   */
  useImperativeHandle(ref, () => ({
    toggleColumnChooser,
    getExportColumnDefinitions,
    onExport,
    onRefresh,
    unSelectRows,
    onClearFilters,
    columnKeys
  }));

  useEffect(() => {
    const immediateRefresh = Boolean(
      refreshDeviceGrid
    );
    setRefreshRequired(immediateRefresh);
  }, [refreshDeviceGrid]);

  useEffect(() => {
    if (includeGlobalSearch && searchKeyword) {
      setRefreshRequired(!refreshRequired);
    }
  }, [includeGlobalSearch, searchKeyword]);

  useEffect((): void => {
    getTotalCount?.(totalRowsCount);
  }, [totalRowsCount]);

  const onColumnSelectionsUpdate = function(columns: ColDef[]): void {
    setIsColumnChanged(true);
    onColumnChooserSave(columns);
    dispatch(dispatchGridColumnsChange(definitionsToState(columns), NAME, SET_GRID_COLUMNS) as any);
  };

  const onColumnStateUpdate = function(state: ColumnState[]): void {
    if (!includeGlobalSearch && !isEmpty(state) && !includeDefaultDefinitions) {
      dispatch(dispatchGridColumnsChange(state, NAME, SET_GRID_COLUMNS) as any);
    }
  };

  const defaultSort: SortModelType = {
    colId : 'name',
    sort  : 'asc'
  };

  const getFilter = (filter) => {
    if (!isNull(filter)) {
      const filterData = Object.entries(filter);
      let globalSearchStr = `(name: *${searchKeyword}* OR esn: *${searchKeyword}*)`;
      let filterStr = '(id:*)';
      filterData.forEach(item => {
        const filterValue = getFilterValue(item[1]);
        if (includeGlobalSearch) {
          globalSearchStr += ` AND ${item[0]}: ${filterValue}`;
        }
        filterStr += ` AND ${item[0]}: (${filterValue})`;
      });
      return includeGlobalSearch ? globalSearchStr : filterStr;
    }
    if (includeGlobalSearch && (hasORkey(searchKeyword) || hasANDkey(searchKeyword))) {
      searchKeyword = addSlashesToQuery(searchKeyword);
      return getQueryValue(searchKeyword);
    }
    return includeGlobalSearch ? getMultipleSearchKeywords(searchKeyword) : DEFAULT_SMART_SEARCH_QUERY;
  };

  const getDeviceMap = useCallback(() => {
    const deviceMap = {};
    ([(editResult|| {}), ...(bulkEditResult as any || [])])?.forEach?.(result => {
      deviceMap[result.id] = result;
    });
    return deviceMap;
  }, [editResult, bulkEditResult]);

  const getSortParams = (sort) => {
    const sortParams = invert(DeviceSortTypes);
    return Boolean(sortParams[sort]);
  };

  // Intercepts the callback from getting the devices to pull out relevant data.
  const callbackInterceptor = useCallback((callback: Function): (result, options) => void => {
    return (result, options) => {
      setIsGridLoaded(true);
      setTotalRowsCount(result?.totalCount);
      const map = getDeviceMap();
      const {items, totalCount} = result;
      const results = {
        items : items.reduce((acc, item) => {
          const {id, version} = item;
          const mapped = map[id];
          // if mapped device verision is higher than the cached
          // version of smart search result, that should be
          // replaced by the updated version(mapped)
          if (version < mapped?.version) {
            acc.push(mapped);
          } else {
            acc.push(item);
          }
          return acc;
        }, []),
        totalCount
      };
      callback?.(results, platformResults);
    };
  }, [getDeviceMap, platformResults]);

  const columnKeys = useMemo(() => {
    if (anySearchForOnlyDisplayedColumns) {
      const map = {deviceType: 'type.title'};
      return (
        defaultDefinitions?.map(({field}) => map[field] || attributeMappings[field] || field).filter(key => (
          !key.includes('batteryPercentageLeft') && !key.includes('lastReportedTime')
        ))
      );
    }
  }, [defaultDefinitions]);

  function getDevices({
    page,
    pageSize,
    sort,
    filter,
    callback
  }) {
    setSortOn(sort);
    const {
      includeHeartBeat,
      includeVin,
      includeInheritableGroups
    } = verifySupplementalCalls({
      columns   : isColumnChanged ? columnDefinitions : definitions,
      isICNUser : isHeartBeat,
      hasVehicleProfile,
      hasInheritableGroups,
      reloadOnVisible
    });
    dispatch(getDevicesBySmartSearch({
      isHeartBeat           : includeHeartBeat,
      hasVehicleProfile     : includeVin,
      hasInheritableGroups  : includeInheritableGroups,
      viewInheritableGroup  : viewInheritableGroup,
      showEOLDevices        : showEOLDevices,
      globalGroup           : globalGroup,
      filter                : getFilter(filter),
      callback              : callbackInterceptor(callback),
      includeSubGroups      : viewSubGroups,
      includeAccountFilter  : !includeGlobalSearch,
      includeSortParams     : isSortByParams ? getSortParams(sort) : false,
      includeParams         : isSortByParams,
      includeGroups         : true,
      includeDeviceSettings : true,
      usePulse              : isCtcAdmin,
      pageId                : page + 1,
      pageSize,
      sort,
      columnKeys,
      isVbuFwEnabled
    }) as any);
  };

  const loadDevices = useCallback(useLoadGridData({
    loadData : getDevices,
    filterModel,
    gridApi
  }), [getDevices, filterModel, gridApi]);

  const onExport = function(downloadParams?: DownloadTemplate) {
    const params: ExportClient = ionGridRef.current?.getExportParams(
      viewSubGroups,
      enableSelectionsExport,
      createModularSearchFilters(modularSearchParams)
    );

    if (enableDefaultSort) {
      params.sortOn = DEFAULT_SORT_PARAM;
    } if (downloadParams?.columns) {
      params.columns = processExportColumns(downloadParams?.columns);
    }

    params.exportLimit          = exportLimit;
    params.gridExportCount      = gridExportCount;
    params.areTotalRowsSelected = areTotalRowsSelected;
    const filters               = searchFilters && !isEmpty(searchFilters) ? searchFilters : params?.filterOn;
    const excludedIds           = excludedRows.map(({id}) => id);
    params.queryString          = createModularSearchQueryString(filters);

    if (hasAnySearch(filters) && hasExportLimit) {
      params.includeSortParams = true;
      params.includeParams     = true;
      params.sortOn            = anySearch;
    } if (hasMaxSearch(filters) && hasExportLimit) {
      params.includeSortParams = true;
      params.includeParams     = true;
      params.maxSearch         = true;
      const maxSearch          = getNewSearchCriteria({
        search   : searchFilters,
        account  : accountID,
        sort     : DEFAULT_SORT_PARAM[0],
        excluded : true,
        viewSubGroups,
        excludedIds,
        isVbuFwEnabled
      });
      params.sortOn            = maxSearch.sortOn;
    } if (excludedRows?.length && hasExportLimit) {
      params.includeSortParams = true;
      params.includeParams     = true;
      params.sortOn            = getExcludeDevicesQuery(
        {
          search    : searchFilters,
          accountId : accountID,
          excludedIds,
          viewSubGroups,
          isVbuFwEnabled
        })?.smartSearch?.search;
    }

    const {
      includeHeartBeat,
      includeVin,
      includeInheritableGroups
    } = verifySupplementalCalls({
      columns   : isColumnChanged ? columnDefinitions : definitions,
      isICNUser : isHeartBeat,
      hasVehicleProfile,
      hasInheritableGroups,
      reloadOnVisible
    });
    
    dispatch(exportDevices(
      params,
      downloadParams?.columns ? downloadParams : undefined,
      isCtcAdmin,
      hasExportLimit,
      platformResults,
      columnKeys,
      includeVin,
      includeInheritableGroups,
      viewInheritableGroup,
      isVbuFwEnabled,
      includeHeartBeat,
      viewSubGroups
    ) as any);
  };

  const onRefresh = function() {
    setRefreshRequired(true);
    setSelectedDevices([]);
  };

  function onBulkEditDevices(devices: Devices) {
    dispatch(editAdminDevices(devices));
  };

  function onDeleteDevices(devices: Devices) {
    dispatch(deleteAdminDevices(devices));
  };

  function onBulkViewDevices(devices: Devices) {
    let displayDevices = cloneDeep(devices);
    if (devices?.length > MAX_VIEW_DEVICES) {
      displayDevices = devices.slice(0, MAX_VIEW_DEVICES);
    }
    const items = getViewDeviceTabs(displayDevices);
    const selectedTab = displayDevices?.[0];
    dispatch(showBulkViewDevices(devices));
    dispatch(setDevicesTabs(items, selectedTab));
    dispatch(setSelectedDevicesTab(selectedTab));
  };

  const onAssetClick = ({asset}: Device) => {
    dispatch(viewAdminAsset(entityId(asset?.href), true, true) as any);
  };

  const unSelectRows = useCallback(() => {
    gridApi?.forEachNode((node) => {
      if (node.selected) {
        node.setSelected(false);
      }
    });
  }, [gridApi]);

  const onSelectionChanged = useCallback((event) => {
    const rowsSelected = event.api.getSelectedRows();
    const uniqItems = getUniqItems(rowsSelected);
    setSelectedDevices(uniqItems);
  }, []);

  const getActionMenu = useMemo(() => (
    <DevicesActionMenu selectedDevices={selectedDevices}
      onSCIUpdateClick={onSCIUpdateClick}
      devicesPermissions={devicesPermissions}
      hasCreateJob={hasCreateJob}
      areTotalRowsSelected={areTotalRowsSelected}/>
  ), [selectedDevices, areTotalRowsSelected]);

  const onFilterChanged = useCallback(function() {
    const filterModel = gridApi?.getFilterModel();
    if (filterModel) {
      dispatch(setFilterModel(filterModel));
    }
  }, [gridApi]);

  const getICNHeartBeatInfo = useCallback((isFirstIcn, heartBeatInfo) => {
    if (!isUndefined(heartBeatInfo)) {
      return isFirstIcn ? formatDateTime(heartBeatInfo?.firstHeartBeatDateTime)
        : formatDateTime(heartBeatInfo?.lastHeartBeatDateTime);
    }
  }, []);

  const getVehicleProfile = useCallback((vinInfo) => {
    if (!isUndefined(vinInfo)) {
      return vinInfo;
    }
  }, []);

  const getAssociatedDeviceId = useCallback((device, type) => {
    const associatedDevices = device?.extensions?.associatedDevices?.[0];
    return (type === SearchFields.MDT && isValidMDTDevice({category: associatedDevices?.deviceCategory})) ||
      (type === SearchFields.LMU && isValidLMUDevice({category: associatedDevices?.deviceCategory}))
      ? associatedDevices?.mobileId : '';
  }, []);

  const onDeviceClick = useCallback((device: Device) => {
    return function() {
      onRowDoubleClicked({data: device}, true);
    };
  }, []);

  /**
   * Converts a collection of devices and converts them to a list of items specific
   * for display within the Admin Devices Grid, while retaining all of the items existing
   * attributes.
   */
  const constructDevicesGridRows = useCallback((devices: Device[], deviceAppIds: GenericObject[]): GridResult[] => {
    attributeMappings.deviceType = devices;
    pageMonitorComplete();
    return devices.map(device => ({
      ...device,
      deviceId              : device?.id,
      deviceName            : device?.name,
      deviceType            : device?.type?.title,
      identifierUI          : getDeviceIdentifier(device),
      accountUI             : EntityLabel.get(device?.account),
      assetName             : device?.asset?.title,
      groupList             : getDeviceGroupsName(device),
      enabledServicesUI     : formatEnabledServices(device),
      statusUI              : translateStatus(device?.status),
      iOnTag                : getIonTag(device),
      appIdUI               : device?.extensions?.appId || null,
      deviceConfigurationUI : device?.deviceConfiguration?.title,
      firmwareVersionUI     : getFirmwareVersion(device, isCtcAdmin),
      configVersionUI       : getConfigVersion(device, true, isCtcAdmin),
      mdnUI                 : getMDN(device),
      iccidUI               : device?.iccid,
      imeiUI                : device?.imei,
      imei2UI               : device?.imei2,
      imsiUI                : device?.imsi,
      isdnUI                : device?.isdn,
      meidUI                : device?.meid,
      configStatusUI        : getFileStatus(device, true),
      firmwareStatusUI      : getFileStatus(device),
      lastReportedTime      : getLastReportedTime(device, usePreferredDateTimeFormater),
      lastAvlCommUI         : formatDateTime(device?.lastAvlCommunication?.locationTime),
      idReportTime          : formatDateTime(device?.extensions?.idReportTimestamp),
      firstIdReportTime     : formatDateTime(device?.idReport?.firstIdReportTime),
      activationDateUI      : formatDateTime(device?.activationDate),
      carrierStatus         : device?.carrierStatus,
      createdOnUI           : formatDateTime(device?.createdOn),
      icnEnabled            : device?.icnEnabled,
      ipAddress             : device?.ipAddress,
      pegBehaviorName       : device?.pegBehavior?.title,
      lastModifiedOnUI      : formatDateTime(device?.lastModifiedOn),
      shipDateUI            : formatDateTime(device?.shipDate),
      firstIcn              : getICNHeartBeatInfo(true, device?.heartBeatInfo),
      lastIcn               : getICNHeartBeatInfo(false, device?.heartBeatInfo),
      vbusStatusUI          : getVbusStatus(device),
      vinUI                 : getVin(device),
      androidOSVersion      : device?.extensions?.androidOSVersion,
      kernelVersion         : device?.extensions?.kernelVersion,
      pendingScriptUI       : getPendingScript(device),
      pendingFirmwareUI     : getPendingFirmware(device),
      eolUI                 : booleanToString(checkEOLDevice(device, deviceAppIds)),
      pendingJpodFWUI       : getPendingJpodFW(device, !isVbuFwEnabled),
      pendingJpodConfigUI   : getPendingJpodConfig(device, !isVbuFwEnabled),
      pendingEdgeHostedUI   : getPendingEdgeHosted(device),
      jpodFirmwareUI        : getJpodFirmware(device, !isVbuFwEnabled),
      jpodConfigUI          : getJpodConfig(device, !isVbuFwEnabled),
      ble3FirmwareUI        : getVersionByPlatformId(device, PLATFORM_IDS.ble3Firmware),
      vbu3FirmwareUI        : getVersionByPlatformId(device, PLATFORM_IDS.vbu3Firmware),
      bspModemFirmwareUI    : getVersionByPlatformId(device, PLATFORM_IDS.bspModemFirmware),
      cellRadioFirmwareUI   : getVersionByPlatformId(device, PLATFORM_IDS.cellRadioFirmware),
      edgeAppVersionUI      : getEdgeAppVersion(device),
      mobileIdUI            : device?.userDefinedMobileId,
      mdtUI                 : getAssociatedDeviceId(device, SearchFields.MDT),
      lmuUI                 : getAssociatedDeviceId(device, SearchFields.LMU),
      vbusModeUI            : device?.extensions?.vbusMode || null,
      peripherals           : getSpreadVal(device?.features?.ATTACHMENTS),
      batteryPercentage     : device?.extensions?.batteryPercentageLeft,
      vehicleProfile        : getVehicleProfile(device?.vinInfo)
    })) as GridResult[];
  }, [usePreferredDateTimeFormater]);

  // invokes to load device view dialog on row double click or any provided grid link
  const onRowDoubleClicked = useCallback((e, onDeviceLinkClick = false) => {
    const {data: device, event} = e;
    const isLinkCell            = event?.target?.getAttribute('col-id') ?? '';
    if (onDeviceLinkClick || !isEmpty(isLinkCell)) {
      dispatch(viewDevice(device) as any);
      dispatch(loadDeviceDetail(device));
      dispatch(viewAdminDevice(device));
    }
  }, []);

  return (
    <IonGrid ref={ionGridRef}
      enableExclude={enableExclude}
      showBulkHeader={showBulkHeader}
      isFilterApplied={isCtcAdmin}
      onRowDoubleClicked={onRowDoubleClicked}
      hasMultiColumnPreferences={hasMultiColumnPreferences}
      isPersist={isPersist}
      excludeRows={excludedRows}
      setExcludedRows={setExcludedRows}
      onBulkActionsClick={onBulkActionsClick}
      rowPerPageTextTransform={rowPerPageTextTransform}
      paginationEnabled
      showCustomPaginationBar
      reloadOnVisible={(isHeartBeat || hasVehicleProfile || hasInheritableGroups) ? reloadOnVisible : []}
      pageSize={gridPageSize}
      setGridPageSize={setGridPageSize}
      pageConstraintProps={{
        constraint : maxLimit,
        entity     : translate('common:entityLabels.devices')
      }}
      refreshRequired={refreshRequired || (refreshGrid && !includeGlobalSearch)}
      setRefreshRequired={setRefreshRequired}
      loadItems={loadDevices}
      filterModel={filterModel}
      augmentItems={constructDevicesGridRows}
      onFilterChanged={onFilterChanged}
      attributeMappings={attributeMappings}
      checkboxesEnabled={isMultiEditEnabled && !includeGlobalSearch}
      rowMenusEnabled={rowMenusEnabled}
      omitHeader={omitHeader}
      omitHeaderCTAs={omitHeaderCTAs}
      onBulkEdit={onBulkEditDevices}
      onBulkDelete={onDeleteDevices}
      onBulkView={onBulkViewDevices}
      actionMenuItems={isCtcAdmin ? getActionMenu : null}
      disableBulkDelete={!hasDeletePermission || !isCtcAdmin || areTotalRowsSelected}
      disableBulkEdit={areTotalRowsSelected}
      disableBulkActions={!hasUpdatePermission}
      columnDefinitions={columnDefinitions}
      defaultDefinitions={defaultDefinitions}
      showColumnChooser={showColumnChooser}
      onColumnChooserSave={onColumnSelectionsUpdate}
      onColumnsUpdated={onColumnStateUpdate}
      onColumnChooserClose={function() {
        toggleColumnChooser(false);
      }}
      columnComponents={{
        rowMenu : item => (
          <DeviceRowEditor showEditAction
            showAuditAction={isCtcAdmin}
            showDeleteAction={isCtcAdmin && !includeGlobalSearch}
            showViewAction={isCtcAdmin}
            device={item?.data}
            refreshDevice={isCtcAdmin}
            devicesPermissions={devicesPermissions}
            showSwapAction={includeSwapDevice && item?.data?.status === EntityStatus.Enabled} />
        ),
        accountName : (item: GridResult) => (
          <AccountCell item={item}
            isCtcAdmin={isCtcAdmin}
            Features={Features}
            accountsPermissions={accountsPermissions}
            refreshInitialization={refreshInitialization}
            onSelectAccount={onSelectAccount} />
        ),
        assetCell : (item: any) => (
          <LinkCell data={item}
            onClick={function() {
              onAssetClick(item?.data);
            }} />
        ),
        deviceCell : item => <LinkCell data={item}
          textTransform={'none'}
          onClick={onDeviceClick(item?.data)} />,
        batteryCell : item => (
          <div className={classes.batteryCell}>
            <ChargeLevel charge={item?.data?.batteryPercentage} isCardVariant isBatteryCell />
          </div>
        ),
        groupsUI : (item: GridResult) => {
          return hasInheritableGroups
            ? <DeviceGroupInheritableLinks item={item} />
            : <DeviceGroupLinks data={item?.data} />;
        },
        scriptIdCell : (item: GridResult) => {
          const filterPlatformIds = item?.data?.fileHistoryPending?.filter((item) =>
            item.platformId === PLATFORM_IDS.config || item.platformId === PLATFORM_IDS.peg2
          );
          return <JobIdLinks data={filterPlatformIds?.[0]} pendingJobIds={filterPlatformIds} isPendingScript/>;
        },
        firmwareIdCell : (item: GridResult) => {
          const filterPlatformIds = item?.data?.fileHistoryPending?.filter((item) =>
            item.platformId === PLATFORM_IDS.firmware
          );
          return <JobIdLinks data={filterPlatformIds?.[0]} pendingJobIds={filterPlatformIds} isPendingFirmware/>;
        },
        eolCellRenderer       : EolCell,
        pendingJpodFWCell     : PendingJobIdLinks,
        pendingJpodConfigCell : PendingJpodConfigLinks,
        pendingEdgeHostedCell : PendingEdgeHostedCell
      }}
      pageSizes={PAGE_SIZES}
      defaultSort={defaultSort}
      onGridReady={onGridReady}
      gridOptions={{onSelectionChanged}}
      hideSubGroups={hideSubGroups}
      viewSubGroups={viewSubGroups}
      viewInheritableGroup={viewInheritableGroup}
      getAreTotalRowsSelected={getAreTotalRowsSelected}
      isTotalRowsSelected={areTotalRowsSelected}
      enableSelectAllCheckbox={enableSelectAllCheckbox}
      toggleViewSubGroups={!includeGlobalSearch ? toggleViewSubGroups : undefined}
      usePreferedPageSize={usePreferedPageSize} />
  );
};

export const getLastReportedTime = (device: Device, usePreferredDateTimeFormater: boolean = false): string => {
  if (usePreferredDateTimeFormater) {
    return dateTimeFormatter(device?.lastReportedTime) as string;
  }
  return formatDateTime(device?.lastReportedTime);
};

const getDeviceGroupsName  = (device: Device): string => (
  device?.groups?.map?.(({title}) => title)?.join(', ') || ''
);

const getIonTag = function({architecture, deviceConfiguration}: Device = {}): string {
  if (architecture === 'SCI') {
    const {title} = deviceConfiguration;
    if (title.startsWith('PTA')) {
      return translate('admin-devices:grid.tags.proxTag');
    } else if (title.endsWith('HT')) {
      return translate('admin-devices:grid.tags.environTag');
    } else if (title.endsWith('XT')) {
      return translate('admin-devices:grid.tags.xtremeTempTag');
    } else if (title.startsWith('SCI_TAG')) {
      return translate('common:booleans.yes');
    } else {
      return translate('common:booleans.no');
    }
  }
  return translate('common:booleans.no');
};

const filterColumns = ({
  columns,
  excludedColumns,
  filterableColumns,
  enableAllHyperLinks,
  enabledHyperLinks,
  isSortByAlphaNumeric
}) => {
  const column = isSortByAlphaNumeric ? sortColumns(columns) : columns;
  return column.filter(column =>
    !excludedColumns.includes(column?.field)
  ).map(column => {
    if (!filterableColumns?.includes(column?.field)) {
      column.filter = false;
      column.filterParams = undefined;
    }
    if (!enableAllHyperLinks && !enabledHyperLinks?.includes(column?.field)) {
      delete column?.cellRenderer;
      delete column?.cellRendererParams;
    }
    return column;
  });
};

const attributeMappings = {
  deviceId              : 'id',
  deviceName            : 'name',
  identifierUI          : 'esn',
  accountUI             : 'account.title',
  statusUI              : 'status',
  assetName             : 'asset.title',
  idReportTime          : 'extensions.idReportTimestamp',
  androidOSVersion      : 'extensions.androidOSVersion',
  kernelVersion         : 'extensions.kernelVersion',
  deviceType            : 'type.title',
  appIdUI               : 'extensions.appId',
  firmwareVersionUI     : 'extensions.firmwareVersion',
  configVersionUI       : 'extensions.configVersion',
  deviceConfigurationUI : 'deviceConfiguration',
  activationDateUI      : 'activationDate',
  shipDateUI            : 'shipDate',
  lastModifiedOnUI      : 'lastModifiedOn',
  mdnUI                 : 'mdn',
  iccidUI               : 'iccid',
  imeiUI                : 'imei',
  imei2UI               : 'imei2',
  imsiUI                : 'imsi',
  isdnUI                : 'isdn',
  meidUI                : 'meid',
  groupList             : 'groups',
  pendingScriptUI       : 'newVersion',
  pendingFirmwareUI     : 'fileHistoryPending.newVersion',
  vbusStatusUI          : 'extensions.vbusEnabledStatus',
  pendingJpodFWUI       : 'Pending JPOD FW',
  pendingJpodConfigUI   : 'Pending JPOD Config',
  pendingEdgeHostedUI   : 'Pending EdgeApp/ Hosted App',
  jpodFirmwareUI        : 'JPOD Firmware',
  jpodConfigUI          : 'JPOD Config',
  ble3FirmwareUI        : 'BLE3 Firmware',
  vbu3FirmwareUI        : 'VBU3 Firmware',
  bspModemFirmwareUI    : 'BSP Modem Firmware',
  cellRadioFirmwareUI   : 'Cell Radio Firmware',
  edgeAppVersionUI      : 'EdgeApp Version',
  mobileIdUI            : 'User Defined Mobile ID',
  mdtUI                 : 'Associated MDT',
  lmuUI                 : 'Associated LMU',
  batteryPercentage     : 'extensions.batteryPercentageLeft',
  vbusModeUI            : 'extensions.vbusMode',
  peripherals           : 'features.ATTACHMENTS',
  vehicleProfile        : 'vinInfo',
  iOnTag                : 'deviceConfiguration.title'
};

const defaultColumn = (headerName: string, field: string): ColDef => ({
  headerName,
  field,
  sortable   : false,
  filter     : false,
  resizable  : true,
  unSortIcon : false,
  hide       : true
});

// Defines a utility to validate if a column can be sorted
const isSortable = (sortable: boolean): boolean => !sortable;

// Defines a utility to validate if a column can be hidden
const hideColumn = (hide: boolean): boolean => !hide;

/**
 * Returns column definitions for desktop admin devices grid
 */
const getColumnDefinitionsDesktop = ({
  excludedColumns,
  filterableColumns,
  enableAllHyperLinks,
  enabledHyperLinks,
  isCtcAdmin,
  isIonSuite,
  isHeartBeat,
  isICNUser,
  showEOLDevices,
  isSortByAlphaNumeric,
  hasVehicleProfile,
  isVbuFwEnabled
}): ColDef[] => {
  const defaultColumns = translate('admin-devices:grid.columns', {returnObjects: true});
  const columns: ColDef[] = [{
    headerName         : translate('admin-devices:grid.columns.deviceId'),
    field              : 'deviceId',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : 'number',
    resizable          : true,
    unSortIcon         : true,
    hide               : !isCtcAdmin,
    filterParams       : defaultNumberFilterParams,
    comparator         : numberSortComparator
  },
  {
    headerName         : translate('admin-devices:grid.columns.deviceName'),
    field              : 'deviceName',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : 'text',
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName         : translate('admin-devices:grid.columns.deviceIdentifier'),
    field              : 'identifierUI',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : 'text',
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName : translate('admin-devices:grid.columns.deviceType'),
    field      : 'deviceType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:grid.columns.userEsnMobileId'),
    field      : 'userESN',
    sortable   : true,
    filter     : 'text',
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName   : translate('admin-devices:grid.columns.eol'),
    field        : 'eolUI',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : false,
    hide         : !showEOLDevices,
    cellRenderer : 'eolCellRenderer'
  },
  {
    headerName : translate('admin-devices:grid.columns.configStatus'),
    field      : 'configStatusUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : true,
    unSortIcon : false
  },
  {
    headerName : translate('admin-devices:grid.columns.firmwareStatus'),
    field      : 'firmwareStatusUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : true,
    unSortIcon : false
  },
  {
    headerName : translate('admin-devices:grid.columns.icnbleivtData'),
    field      : 'enabledServicesUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : !isCtcAdmin
  },
  {
    headerName         : translate('admin-devices:grid.columns.asset'),
    field              : 'assetName',
    cellRenderer       : 'assetCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  },
  {
    headerName         : translate('admin-devices:grid.columns.account'),
    field              : 'accountUI',
    cellRenderer       : 'accountName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  },
  {
    headerName : translate('admin-devices:grid.columns.iOnTag'),
    field      : 'iOnTag',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : isCtcAdmin
  },
  {
    headerName   : translate('admin-devices:grid.columns.status'),
    field        : 'statusUI',
    sortable     : true,
    filter       : 'agSetColumnFilter',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : getStatusFilterParams()
  },
  {
    headerName   : translate('admin-devices:grid.columns.airId'),
    field        : 'airId',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-devices:grid.columns.appId'),
    field      : 'appIdUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate('admin-devices:grid.columns.esn'),
    field        : 'esn',
    sortable     : isSortable(isIonSuite),
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : hideColumn(isIonSuite),
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-devices:grid.columns.serialNumber'),
    field        : 'serialNumber',
    sortable     : isSortable(isIonSuite),
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-devices:grid.columns.iccid'),
    field      : isCtcAdmin ? 'iccidUI' : 'iccid',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate('admin-devices:grid.columns.imei'),
    field        : isCtcAdmin ? 'imeiUI' : 'imei',
    sortable     : isSortable(isIonSuite),
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-devices:grid.columns.imei2'),
    field      : 'imei2UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.imsi'),
    field      : isCtcAdmin ? 'imsiUI' : 'imsi',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.isdn'),
    field      : isCtcAdmin ? 'isdnUI' : 'isdn',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.mdn'),
    field      : 'mdnUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.meid'),
    field      : isCtcAdmin ? 'meidUI' : 'meid',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.deviceConfiguration'),
    field      : 'deviceConfigurationUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.firmwareVersion'),
    field      : 'firmwareVersionUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.configVersion'),
    field      : 'configVersionUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.category'),
    field      : 'category',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.architecture'),
    field      : 'architecture',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName         : translate('admin-devices:grid.columns.groups'),
    field              : 'groupList',
    cellRenderer       : 'groupsUI',
    cellRendererParams : ({data}) => ({data}),
    headerTooltip      : !isIonSuite ? translate('admin-devices:grid.tooltip.groupList') : null,
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : true
  },
  {
    headerName    : translate('admin-devices:grid.columns.lastEventTime'),
    field         : 'lastReportedTime',
    sortable      : true,
    filter        : false,
    resizable     : true,
    unSortIcon    : true,
    hide          : false,
    headerTooltip : !isIonSuite ? translate('admin-devices:grid.tooltip.lastEventTime') : null
  },
  {
    headerName : translate(`admin-devices:grid.columns.${isVbuFwEnabled ? 'jpodFirmware' : 'vbuFirmware'}`),
    field      : 'jpodFirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate('admin-devices:grid.columns.jpodConfig'),
    field      : 'jpodConfigUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate('admin-devices:grid.columns.ble3Firmware'),
    field      : 'ble3FirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate('admin-devices:grid.columns.bspModemFirmware'),
    field      : 'bspModemFirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate('admin-devices:grid.columns.cellRadioFirmware'),
    field      : 'cellRadioFirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate('admin-devices:grid.columns.edgeAppVersion'),
    field      : 'edgeAppVersionUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate('admin-devices:grid.columns.mobileId'),
    field      : 'mobileIdUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate('admin-devices:grid.columns.vbusMode'),
    field      : 'vbusModeUI',
    sortable   : !isVbuFwEnabled,
    filter     : false,
    resizable  : true,
    unSortIcon : !isVbuFwEnabled,
    hide       : !isCtcAdmin
  },
  {
    headerName         : translate('admin-devices:grid.columns.pendingFirmware'),
    field              : 'pendingFirmwareUI',
    cellRenderer       : 'firmwareIdCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : !isCtcAdmin
  },
  {
    headerName         : translate('admin-devices:grid.columns.pendingScript'),
    field              : 'pendingScriptUI',
    cellRenderer       : 'scriptIdCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : !isCtcAdmin
  },
  {
    headerName         : translate(`admin-devices:grid.columns.${isVbuFwEnabled ? 'pendingJpodFW' : 'pendingVbuFW'}`),
    field              : 'pendingJpodFWUI',
    cellRenderer       : 'pendingJpodFWCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : !isCtcAdmin
  },
  {
    headerName         : translate('admin-devices:grid.columns.pendingJpodConfig'),
    field              : 'pendingJpodConfigUI',
    cellRenderer       : 'pendingJpodConfigCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : !isCtcAdmin
  },
  {
    headerName         : translate('admin-devices:grid.columns.pendingEdgeHostedApps'),
    field              : 'pendingEdgeHostedUI',
    cellRenderer       : 'pendingEdgeHostedCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : !isCtcAdmin
  },
  {
    headerName    : translate('admin-devices:grid.columns.lastAvlTime'),
    field         : 'lastAvlCommUI',
    sortable      : false,
    filter        : false,
    resizable     : true,
    unSortIcon    : false,
    hide          : true,
    headerTooltip : translate('admin-devices:grid.tooltip.lastAvlTime')
  },
  {
    headerName   : translate('admin-devices:grid.columns.androidOSVersion'),
    field        : 'androidOSVersion',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-devices:grid.columns.kernelVersion'),
    field        : 'kernelVersion',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-devices:grid.columns.mdt'),
    field        : 'mdtUI',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-devices:grid.columns.lmu'),
    field        : 'lmuUI',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-devices:grid.columns.peripherals'),
    field      : 'peripherals',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName         : translate('admin-devices:grid.columns.batteryPercentage'),
    field              : 'batteryPercentage',
    cellRenderer       : 'batteryCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : true
  }];
  if (isCtcAdmin) {
    columns.push(defaultColumn(defaultColumns.vin, 'vinUI'));
    columns.push(
      getIdReportTimeColumnDefinition(),
      getDefaultColumnWithSort(defaultColumns.vbusStatus, 'vbusStatusUI')
    );
    columns.push(defaultColumn(defaultColumns.firstIdReportTime, 'firstIdReportTime'));
  }
  if (isHeartBeat) {
    columns.push(
      getDefaultColumnWithSortPayload(defaultColumns.activationDate, 'activationDateUI'),
      defaultColumn(defaultColumns.carrierStatus, 'carrierStatus'),
      defaultColumn(defaultColumns.createdOn, 'createdOnUI'),
      defaultColumn(defaultColumns.icnEnabled, 'icnEnabled'),
      getDefaultColumnWithSort(defaultColumns.ipAddress, 'ipAddress'),
      defaultColumn(defaultColumns.pegBehavior, 'pegBehaviorName'),
      getDefaultColumnWithSort(defaultColumns.lastModifiedOn, 'lastModifiedOnUI'),
      getDefaultColumnWithSort(defaultColumns.shipDate, 'shipDateUI')
    );
  }
  if (hasVehicleProfile) {
    columns.push(defaultColumn(defaultColumns.vehicleProfile, 'vehicleProfile'));
  }
  if (isICNUser) {
    columns.push(
      defaultColumn(defaultColumns.firstIcn, 'firstIcn'),
      defaultColumn(defaultColumns.lastIcn, 'lastIcn')
    );
  }
  if (isVbuFwEnabled) {
    columns.push(
      getDefaultVBUSColumn(defaultColumns.vbu3Firmware, 'vbu3FirmwareUI', isCtcAdmin)
    );
  }
  return filterColumns({
    columns,
    excludedColumns,
    filterableColumns,
    enableAllHyperLinks,
    enabledHyperLinks,
    isSortByAlphaNumeric
  });
};

const getDefaultVBUSColumn = (headerName: string, field: string, hideColumn: boolean): ColDef => ({
  headerName,
  field,
  sortable   : true,
  filter     : false,
  resizable  : true,
  unSortIcon : true,
  hide       : !hideColumn
});

const getDefaultColumnWithSort = (headerName: string, field: string): ColDef => ({
  headerName,
  field,
  sortable   : true,
  filter     : false,
  resizable  : true,
  unSortIcon : true,
  hide       : true
});

const getDefaultColumnWithSortPayload = (headerName: string, field: string): ColDef => ({
  headerName,
  field,
  sortable   : true,
  filter     : false,
  resizable  : true,
  unSortIcon : true,
  hide       : true
});

// Generates the column definition for the ID report time column.
const getIdReportTimeColumnDefinition = (): ColDef => ({
  headerName    : translate('admin-devices:grid.columns.idReportTime'),
  field         : 'idReportTime',
  sortable      : true,
  filter        : false,
  resizable     : true,
  unSortIcon    : true,
  hide          : true,
  headerTooltip : translate('admin-devices:grid.tooltip.idReportTime')
});

/**
 * Returns column definitions for mobile admin devices grid
 */
const getColumnDefinitionsMobile = ({
  excludedColumns,
  enableAllHyperLinks,
  enabledHyperLinks,
  filterableColumns,
  isCtcAdmin,
  isIonSuite,
  isHeartBeat,
  hasVehicleProfile,
  isICNUser,
  showEOLDevices,
  isSortByAlphaNumeric,
  isVbuFwEnabled
}): ColDef[] => {
  const defaultColumns = translate('admin-devices:grid.columns', {returnObjects: true});
  const columns: ColDef[] = [{
    headerName         : translate('admin-devices:grid.columns.deviceId'),
    field              : 'deviceId',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : 'number',
    resizable          : true,
    unSortIcon         : true,
    hide               : !isCtcAdmin,
    filterParams       : defaultNumberFilterParams,
    comparator         : numberSortComparator
  },
  {
    headerName         : translate('admin-devices:grid.columns.deviceName'),
    field              : 'deviceName',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : 'text',
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName         : translate('admin-devices:grid.columns.deviceIdentifier'),
    field              : 'identifierUI',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : 'text',
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName : translate('admin-devices:grid.columns.deviceType'),
    field      : 'deviceType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:grid.columns.userEsnMobileId'),
    field      : 'userESN',
    sortable   : true,
    filter     : 'text',
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:grid.columns.configStatus'),
    field      : 'configStatusUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : true,
    unSortIcon : false
  },
  {
    headerName : translate('admin-devices:grid.columns.firmwareStatus'),
    field      : 'firmwareStatusUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : true,
    unSortIcon : false
  },
  {
    headerName   : translate('admin-devices:grid.columns.eol'),
    field        : 'eolUI',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : false,
    hide         : !showEOLDevices,
    cellRenderer : 'eolCellRenderer'
  },
  {
    headerName : translate('admin-devices:grid.columns.icnbleivtData'),
    field      : 'enabledServicesUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : !isCtcAdmin
  },
  {
    headerName         : translate('admin-devices:grid.columns.asset'),
    field              : 'assetName',
    cellRenderer       : 'assetCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  },
  {
    headerName         : translate('admin-devices:grid.columns.account'),
    field              : 'accountUI',
    cellRenderer       : 'accountName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  },
  {
    headerName : translate('admin-devices:grid.columns.iOnTag'),
    field      : 'iOnTag',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : isCtcAdmin
  },
  {
    headerName   : translate('admin-devices:grid.columns.status'),
    field        : 'statusUI',
    sortable     : true,
    filter       : 'agSetColumnFilter',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : getStatusFilterParams()
  },
  {
    headerName : translate(`admin-devices:grid.columns.${isVbuFwEnabled ? 'jpodFirmware' : 'vbuFirmware'}`),
    field      : 'jpodFirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate('admin-devices:grid.columns.jpodConfig'),
    field      : 'jpodConfigUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate('admin-devices:grid.columns.ble3Firmware'),
    field      : 'ble3FirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate('admin-devices:grid.columns.bspModemFirmware'),
    field      : 'bspModemFirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate('admin-devices:grid.columns.cellRadioFirmware'),
    field      : 'cellRadioFirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate('admin-devices:grid.columns.edgeAppVersion'),
    field      : 'edgeAppVersionUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate('admin-devices:grid.columns.mobileId'),
    field      : 'mobileIdUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate('admin-devices:grid.columns.vbusMode'),
    field      : 'vbusModeUI',
    sortable   : !isVbuFwEnabled,
    filter     : false,
    resizable  : true,
    unSortIcon : !isVbuFwEnabled,
    hide       : !isCtcAdmin
  },
  {
    headerName         : translate('admin-devices:grid.columns.pendingFirmware'),
    field              : 'pendingFirmwareUI',
    cellRenderer       : 'firmwareIdCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : !isCtcAdmin
  },
  {
    headerName         : translate('admin-devices:grid.columns.pendingScript'),
    field              : 'pendingScriptUI',
    cellRenderer       : 'scriptIdCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : !isCtcAdmin
  },
  {
    headerName         : translate(`admin-devices:grid.columns.${isVbuFwEnabled ? 'pendingJpodFW' : 'pendingVbuFW'}`),
    field              : 'pendingJpodFWUI',
    cellRenderer       : 'pendingJpodFWCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : !isCtcAdmin
  },
  {
    headerName         : translate('admin-devices:grid.columns.pendingJpodConfig'),
    field              : 'pendingJpodConfigUI',
    cellRenderer       : 'pendingJpodConfigCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : !isCtcAdmin
  },
  {
    headerName         : translate('admin-devices:grid.columns.pendingEdgeHostedApps'),
    field              : 'pendingEdgeHostedUI',
    cellRenderer       : 'pendingEdgeHostedCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : !isCtcAdmin
  },
  {
    headerName   : translate('admin-devices:grid.columns.airId'),
    field        : 'airId',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-devices:grid.columns.appId'),
    field      : 'appIdUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate('admin-devices:grid.columns.esn'),
    field        : 'esn',
    sortable     : isSortable(isIonSuite),
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : hideColumn(isIonSuite),
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-devices:grid.columns.serialNumber'),
    field        : 'serialNumber',
    sortable     : isSortable(isIonSuite),
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-devices:grid.columns.iccid'),
    field      : isCtcAdmin ? 'iccidUI' : 'iccid',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate('admin-devices:grid.columns.imei'),
    field        : isCtcAdmin ? 'imeiUI' : 'imei',
    sortable     : isSortable(isIonSuite),
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-devices:grid.columns.imei2'),
    field      : 'imei2UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.imsi'),
    field      : isCtcAdmin ? 'imsiUI' : 'imsi',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.isdn'),
    field      : isCtcAdmin ? 'isdnUI' : 'isdn',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.mdn'),
    field      : 'mdnUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.meid'),
    field      : isCtcAdmin ? 'meidUI' : 'meid',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.deviceConfiguration'),
    field      : 'deviceConfigurationUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.firmwareVersion'),
    field      : 'firmwareVersionUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.configVersion'),
    field      : 'configVersionUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.category'),
    field      : 'category',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('admin-devices:grid.columns.architecture'),
    field      : 'architecture',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName         : translate('admin-devices:grid.columns.groups'),
    field              : 'groupList',
    cellRenderer       : 'groupsUI',
    cellRendererParams : ({data}) => ({data}),
    headerTooltip      : !isIonSuite ? translate('admin-devices:grid.tooltip.groupList') : null,
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : true
  },
  {
    headerName    : translate('admin-devices:grid.columns.lastEventTime'),
    field         : 'lastReportedTime',
    sortable      : true,
    filter        : false,
    resizable     : true,
    unSortIcon    : true,
    hide          : true,
    headerTooltip : !isIonSuite ? translate('admin-devices:grid.tooltip.lastEventTime') : null
  },
  {
    headerName    : translate('admin-devices:grid.columns.lastAvlTime'),
    field         : 'lastAvlCommUI',
    sortable      : false,
    filter        : false,
    resizable     : true,
    unSortIcon    : false,
    hide          : true,
    headerTooltip : translate('admin-devices:grid.tooltip.lastAvlTime')
  },
  {
    headerName   : translate('admin-devices:grid.columns.androidOSVersion'),
    field        : 'androidOSVersion',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-devices:grid.columns.kernelVersion'),
    field        : 'kernelVersion',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-devices:grid.columns.mdt'),
    field        : 'mdtUI',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-devices:grid.columns.lmu'),
    field        : 'lmuUI',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-devices:grid.columns.peripherals'),
    field      : 'peripherals',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName         : translate('admin-devices:grid.columns.batteryPercentage'),
    field              : 'batteryPercentage',
    cellRenderer       : 'batteryCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : true
  }];
  if (isCtcAdmin) {
    columns.push(defaultColumn(defaultColumns.vin, 'vinUI'));
    columns.push(
      getIdReportTimeColumnDefinition(),
      getDefaultColumnWithSort(defaultColumns.vbusStatus, 'vbusStatusUI')
    );
    columns.push(defaultColumn(defaultColumns.firstIdReport, 'firstIdReport'));
  }
  if (isHeartBeat) {
    columns.push(
      getDefaultColumnWithSortPayload(defaultColumns.activationDate, 'activationDateUI'),
      defaultColumn(defaultColumns.carrierStatus, 'carrierStatus'),
      defaultColumn(defaultColumns.createdOn, 'createdOnUI'),
      defaultColumn(defaultColumns.icnEnabled, 'icnEnabled'),
      getDefaultColumnWithSort(defaultColumns.ipAddress, 'ipAddress'),
      defaultColumn(defaultColumns.pegBehavior, 'pegBehaviorName'),
      getDefaultColumnWithSort(defaultColumns.lastModifiedOn, 'lastModifiedOnUI'),
      getDefaultColumnWithSort(defaultColumns.shipDate, 'shipDateUI')
    );
  }
  if (hasVehicleProfile) {
    columns.push(defaultColumn(defaultColumns.vehicleProfile, 'vehicleProfile'));
  }
  if (isICNUser) {
    columns.push(
      defaultColumn(defaultColumns.firstIcn, 'firstIcn'),
      defaultColumn(defaultColumns.lastIcn, 'lastIcn')
    );
  }
  if (isVbuFwEnabled) {
    columns.push(
      getDefaultVBUSColumn(defaultColumns.vbu3Firmware, 'vbu3FirmwareUI', isCtcAdmin)
    );
  }
  return filterColumns({
    columns,
    excludedColumns,
    filterableColumns,
    enableAllHyperLinks,
    enabledHyperLinks,
    isSortByAlphaNumeric
  });
};

/**
 * Returns column definitions for the admin devices grid in global search desktop mode.
 */
const getColumnDefinitionsDesktopSearch = (): ColDef[] => {
  return [{
    headerName         : translate('admin-devices:grid.columns.deviceId'),
    field              : 'deviceId',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultNumberFilterParams,
    comparator         : numberSortComparator
  },
  {
    headerName         : translate('admin-devices:grid.columns.deviceName'),
    field              : 'name',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName         : translate('admin-devices:grid.columns.deviceIdentifier'),
    field              : 'identifierUI',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName : translate('admin-devices:grid.columns.deviceType'),
    field      : 'deviceType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName   : translate('admin-devices:grid.columns.airId'),
    field        : 'airId',
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  },
  {
    headerName         : translate('admin-devices:grid.columns.asset'),
    field              : 'assetName',
    cellRenderer       : 'assetCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  },
  {
    headerName   : translate('admin-devices:grid.columns.status'),
    field        : 'statusUI',
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultStatusFilterParams
  },
  {
    headerName         : translate('admin-devices:grid.columns.account'),
    field              : 'accountUI',
    cellRenderer       : 'accountName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  }];
};

const getColumnDefinitions = ({
  excludedColumns,
  filterableColumns,
  enableAllHyperLinks,
  enabledHyperLinks,
  includeGlobalSearch,
  isCtcAdmin,
  isIonSuite,
  isHeartBeat,
  hasVehicleProfile,
  isICNUser,
  showEOLDevices,
  isSortByAlphaNumeric,
  isVbuFwEnabled
}) => ({
  desktop : resolveDefinitions(includeGlobalSearch
    ? getColumnDefinitionsDesktopSearch()
    : getColumnDefinitionsDesktop({
      excludedColumns,
      filterableColumns,
      enableAllHyperLinks,
      enabledHyperLinks,
      isCtcAdmin,
      isIonSuite,
      isHeartBeat,
      hasVehicleProfile,
      isICNUser,
      showEOLDevices,
      isSortByAlphaNumeric,
      isVbuFwEnabled
    })),
  mobile : resolveDefinitions(includeGlobalSearch
    ? getColumnDefinitionsDesktopSearch()
    : getColumnDefinitionsMobile({
      excludedColumns,
      filterableColumns,
      enableAllHyperLinks,
      enabledHyperLinks,
      isCtcAdmin,
      isIonSuite,
      isHeartBeat,
      hasVehicleProfile,
      isICNUser,
      showEOLDevices,
      isSortByAlphaNumeric,
      isVbuFwEnabled
    }))
});

export default forwardRef(DevicesGrid);
